import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { splitVisibleItems } from "shared/utils";

import Button from "features/components/Button";
import Chip from "features/components/Chip";
import { DialogWithTrigger } from "features/components/Dialog";
import ValuesSearchList from "features/ui/ValuesSearchList";

const MAX_VISIBLE_CHIPS_DEFAULT = 2;

interface Props {
  title: string;
  values?: string[] | JSX.Element[];
  maxVisible?: number;
  defaultWrapper?: boolean;
  clickableChips?: boolean;
}

const ChipList = ({
  title,
  values,
  maxVisible = MAX_VISIBLE_CHIPS_DEFAULT,
  defaultWrapper = true,
  clickableChips = false,
}: Props) => {
  const { t } = useTranslation("common");

  if (!values || values.length === 0) {
    return null;
  }

  const { visible: valuesPerChip, hidden: valuesForMore } = splitVisibleItems<
    string | JSX.Element
  >(values, maxVisible);
  const moreLabel = t("chip_list.more", { count: valuesForMore.length });

  const moreButton = (
    <Button
      size="small"
      label={moreLabel}
      key={`btn-${moreLabel}`}
      variant="text"
    />
  );

  return (
    <ConditionalWrapper condition={defaultWrapper}>
      {valuesPerChip.map((chipContent, idx) =>
        typeof chipContent === "string" ? (
          <Chip
            label={chipContent}
            key={`chip-${idx}`}
            clickable={clickableChips}
          />
        ) : (
          chipContent
        )
      )}
      {valuesForMore.length > 0 && (
        <DialogWithTrigger
          triggerElement={moreButton}
          title={title}
          // casting valuesForMore to string[] is ok, since we pass JSX.Element[] values only for filters,
          // but we always show all filters, never only X filters + more modal
        >
          <ValuesSearchList title={title} values={valuesForMore as string[]} />
        </DialogWithTrigger>
      )}
    </ConditionalWrapper>
  );
};

export default ChipList;

const ConditionalWrapper = ({
  condition,
  children,
}: {
  condition: boolean;
  children: ReactNode;
}) =>
  condition ? (
    <span className="inline-flex gap-2 flex-wrap items-baseline">
      {children}
    </span>
  ) : (
    <>{children}</>
  );
